import React, { useState, useEffect } from "react";

import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import CircularProgress from "@material-ui/core/CircularProgress";
import { i18n } from "../../translate/i18n";
import { MenuItem, Select } from "@material-ui/core";
import { Visibility, VisibilityOff } from "@material-ui/icons";
import { InputAdornment, IconButton } from "@material-ui/core";
import QueueSelectSingle from "../../components/QueueSelectSingle";
import {
    usePanelStyles,
    ModalTitle,
    FormField,
    FormGrid,
    FormSection,
} from "../QueueIntegrationModal/panelKit";

import api from "../../services/api";
import toastError from "../../errors/toastError";

const useStyles = makeStyles(theme => ({
    // QueueSelectSingle traz rótulo e margens próprios: aqui ele entra no ritmo dos outros campos
    queueField: {
        minWidth: 0,
        "& > div": { marginTop: "0 !important" },
        "& .MuiFormControl-root": { margin: 0 },
        "& .MuiTypography-root": {
            marginBottom: 6,
            fontSize: 12.5,
            fontWeight: 600,
            lineHeight: 1.35,
            color: theme.palette.text.secondary,
        },
    },
    promptInput: { "& textarea": { lineHeight: 1.55 } },
}));

const PromptSchema = Yup.object().shape({
    name: Yup.string().min(5, "Muito curto!").max(100, "Muito longo!").required("Obrigatório"),
    prompt: Yup.string().min(50, "Muito curto!").required("Descreva o treinamento para Inteligência Artificial"),
    voice: Yup.string().required("Informe o modo para Voz"),
    max_tokens: Yup.number().required("Informe o número máximo de tokens"),
    temperature: Yup.number().required("Informe a temperatura"),
    apikey: Yup.string().required("Informe a API Key"),
    queueId: Yup.number().required("Informe a fila"),
    max_messages: Yup.number().required("Informe o número máximo de mensagens")
});

const PromptModal = ({ open, onClose, promptId }) => {
    const classes = useStyles();
    const panel = usePanelStyles();
    const [selectedVoice, setSelectedVoice] = useState("texto");
    const [showApiKey, setShowApiKey] = useState(false);

    const handleToggleApiKey = () => {
        setShowApiKey(!showApiKey);
    };

    const initialState = {
        name: "",
        prompt: "",
        voice: "texto",
        voiceKey: "",
        voiceRegion: "",
        maxTokens: 100,
        temperature: 1,
        apiKey: "",
        queueId: null,
        maxMessages: 10
    };

    const [prompt, setPrompt] = useState(initialState);

    useEffect(() => {
        const fetchPrompt = async () => {
            if (!promptId) {
                setPrompt(initialState);
                return;
            }
            try {
                const { data } = await api.get(`/prompt/${promptId}`);
                setPrompt(prevState => {
                    return { ...prevState, ...data };
                });
                setSelectedVoice(data.voice);
            } catch (err) {
                toastError(err);
            }
        };

        fetchPrompt();
    }, [promptId, open]);

    const handleClose = () => {
        setPrompt(initialState);
        setSelectedVoice("texto");
        onClose();
    };

    const handleChangeVoice = (e) => {
        setSelectedVoice(e.target.value);
    };

    const handleSavePrompt = async values => {
        const promptData = { ...values, voice: selectedVoice };
        if (!values.queueId) {
            toastError("Informe o setor");
            return;
        }
        try {
            if (promptId) {
                await api.put(`/prompt/${promptId}`, promptData);
            } else {
                await api.post("/prompt", promptData);
            }
            toast.success(i18n.t("promptModal.success"));
        } catch (err) {
            toastError(err);
        }
        handleClose();
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="md"
            scroll="paper"
            fullWidth
            aria-labelledby="form-dialog-title"
        >
            <ModalTitle
                id="form-dialog-title"
                title={promptId
                    ? `${i18n.t("promptModal.title.edit")}`
                    : `${i18n.t("promptModal.title.add")}`}
                subtitle="Instruções e parâmetros da IA que responde na fila escolhida."
                onClose={handleClose}
            />
            <Formik
                initialValues={prompt}
                enableReinitialize={true}
                onSubmit={(values, actions) => {
                    setTimeout(() => {
                        handleSavePrompt(values);
                        actions.setSubmitting(false);
                    }, 400);
                }}
            >
                {({ touched, errors, isSubmitting, values }) => (
                    <Form className={panel.form}>
                        <DialogContent>
                            <FormSection title="Identificação">
                                <FormGrid>
                                    <FormField
                                        label={i18n.t("promptModal.form.name")}
                                        required
                                        error={touched.name && errors.name}
                                    >
                                        <Field
                                            as={TextField}
                                            name="name"
                                            error={touched.name && Boolean(errors.name)}
                                            variant="outlined"
                                            size="small"
                                            fullWidth
                                            required
                                        />
                                    </FormField>
                                    <div className={classes.queueField}>
                                        <QueueSelectSingle />
                                    </div>
                                    <FormField
                                        full
                                        label={i18n.t("promptModal.form.apikey")}
                                        required
                                        error={touched.apiKey && errors.apiKey}
                                    >
                                        <Field
                                            as={TextField}
                                            name="apiKey"
                                            type={showApiKey ? 'text' : 'password'}
                                            autoComplete="new-password"
                                            error={touched.apiKey && Boolean(errors.apiKey)}
                                            variant="outlined"
                                            size="small"
                                            fullWidth
                                            required
                                            InputProps={{
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        <IconButton
                                                            size="small"
                                                            edge="end"
                                                            aria-label="Mostrar/ocultar chave"
                                                            onClick={handleToggleApiKey}
                                                        >
                                                            {showApiKey ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                                                        </IconButton>
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />
                                    </FormField>
                                </FormGrid>
                            </FormSection>

                            <FormSection
                                title="Instruções"
                                description="Quem a IA é, o que pode responder e quando deve passar para um atendente."
                            >
                                <FormField
                                    label={i18n.t("promptModal.form.prompt")}
                                    required
                                    error={touched.prompt && errors.prompt}
                                >
                                    <Field
                                        as={TextField}
                                        name="prompt"
                                        error={touched.prompt && Boolean(errors.prompt)}
                                        variant="outlined"
                                        size="small"
                                        fullWidth
                                        required
                                        rows={10}
                                        multiline={true}
                                        className={classes.promptInput}
                                    />
                                </FormField>
                            </FormSection>

                            <FormSection
                                title="Voz"
                                description="Em “Texto” a IA só escreve; com uma voz escolhida, informe a chave e a região do serviço de voz."
                            >
                                <FormGrid columns={3}>
                                    <FormField label={i18n.t("promptModal.form.voice")}>
                                        <Select
                                            id="type-select"
                                            variant="outlined"
                                            margin="dense"
                                            fullWidth
                                            name="voice"
                                            value={selectedVoice}
                                            onChange={handleChangeVoice}
                                            multiple={false}
                                        >
                                            <MenuItem key={"texto"} value={"texto"}>
                                                Texto
                                            </MenuItem>
                                            <MenuItem key={"pt-BR-FranciscaNeural"} value={"pt-BR-FranciscaNeural"}>
                                                Francisa
                                            </MenuItem>
                                            <MenuItem key={"pt-BR-AntonioNeural"} value={"pt-BR-AntonioNeural"}>
                                                Antônio
                                            </MenuItem>
                                            <MenuItem key={"pt-BR-BrendaNeural"} value={"pt-BR-BrendaNeural"}>
                                                Brenda
                                            </MenuItem>
                                            <MenuItem key={"pt-BR-DonatoNeural"} value={"pt-BR-DonatoNeural"}>
                                                Donato
                                            </MenuItem>
                                            <MenuItem key={"pt-BR-ElzaNeural"} value={"pt-BR-ElzaNeural"}>
                                                Elza
                                            </MenuItem>
                                            <MenuItem key={"pt-BR-FabioNeural"} value={"pt-BR-FabioNeural"}>
                                                Fábio
                                            </MenuItem>
                                            <MenuItem key={"pt-BR-GiovannaNeural"} value={"pt-BR-GiovannaNeural"}>
                                                Giovanna
                                            </MenuItem>
                                            <MenuItem key={"pt-BR-HumbertoNeural"} value={"pt-BR-HumbertoNeural"}>
                                                Humberto
                                            </MenuItem>
                                            <MenuItem key={"pt-BR-JulioNeural"} value={"pt-BR-JulioNeural"}>
                                                Julio
                                            </MenuItem>
                                            <MenuItem key={"pt-BR-LeilaNeural"} value={"pt-BR-LeilaNeural"}>
                                                Leila
                                            </MenuItem>
                                            <MenuItem key={"pt-BR-LeticiaNeural"} value={"pt-BR-LeticiaNeural"}>
                                                Letícia
                                            </MenuItem>
                                            <MenuItem key={"pt-BR-ManuelaNeural"} value={"pt-BR-ManuelaNeural"}>
                                                Manuela
                                            </MenuItem>
                                            <MenuItem key={"pt-BR-NicolauNeural"} value={"pt-BR-NicolauNeural"}>
                                                Nicolau
                                            </MenuItem>
                                            <MenuItem key={"pt-BR-ValerioNeural"} value={"pt-BR-ValerioNeural"}>
                                                Valério
                                            </MenuItem>
                                            <MenuItem key={"pt-BR-YaraNeural"} value={"pt-BR-YaraNeural"}>
                                                Yara
                                            </MenuItem>
                                        </Select>
                                    </FormField>
                                    <FormField
                                        label={i18n.t("promptModal.form.voiceKey")}
                                        error={touched.voiceKey && errors.voiceKey}
                                    >
                                        <Field
                                            as={TextField}
                                            name="voiceKey"
                                            error={touched.voiceKey && Boolean(errors.voiceKey)}
                                            variant="outlined"
                                            size="small"
                                            fullWidth
                                        />
                                    </FormField>
                                    <FormField
                                        label={i18n.t("promptModal.form.voiceRegion")}
                                        error={touched.voiceRegion && errors.voiceRegion}
                                    >
                                        <Field
                                            as={TextField}
                                            name="voiceRegion"
                                            error={touched.voiceRegion && Boolean(errors.voiceRegion)}
                                            variant="outlined"
                                            size="small"
                                            fullWidth
                                        />
                                    </FormField>
                                </FormGrid>
                            </FormSection>

                            <FormSection title="Limites">
                                <FormGrid columns={3}>
                                    <FormField
                                        label={i18n.t("promptModal.form.temperature")}
                                        error={touched.temperature && errors.temperature}
                                    >
                                        <Field
                                            as={TextField}
                                            name="temperature"
                                            error={touched.temperature && Boolean(errors.temperature)}
                                            variant="outlined"
                                            size="small"
                                            fullWidth
                                        />
                                    </FormField>
                                    <FormField
                                        label={i18n.t("promptModal.form.max_tokens")}
                                        error={touched.maxTokens && errors.maxTokens}
                                    >
                                        <Field
                                            as={TextField}
                                            name="maxTokens"
                                            error={touched.maxTokens && Boolean(errors.maxTokens)}
                                            variant="outlined"
                                            size="small"
                                            fullWidth
                                        />
                                    </FormField>
                                    <FormField
                                        label={i18n.t("promptModal.form.max_messages")}
                                        error={touched.maxMessages && errors.maxMessages}
                                    >
                                        <Field
                                            as={TextField}
                                            name="maxMessages"
                                            error={touched.maxMessages && Boolean(errors.maxMessages)}
                                            variant="outlined"
                                            size="small"
                                            fullWidth
                                        />
                                    </FormField>
                                </FormGrid>
                            </FormSection>
                        </DialogContent>
                        <DialogActions>
                            <Button
                                onClick={handleClose}
                                disabled={isSubmitting}
                            >
                                {i18n.t("promptModal.buttons.cancel")}
                            </Button>
                            <Button
                                type="submit"
                                color="primary"
                                disabled={isSubmitting}
                                variant="contained"
                                className={panel.busyWrap}
                            >
                                {promptId
                                    ? `${i18n.t("promptModal.buttons.okEdit")}`
                                    : `${i18n.t("promptModal.buttons.okAdd")}`}
                                {isSubmitting && (
                                    <CircularProgress
                                        size={20}
                                        className={panel.busy}
                                    />
                                )}
                            </Button>
                        </DialogActions>
                    </Form>
                )}
            </Formik>
        </Dialog>
    );
};

export default PromptModal;
